import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, ClipboardCheck, LogOut, Mic, Sparkles, X } from 'components/Icons';
import FocusRing from '../components/FocusRing';
import { findTarget, flightToStore } from '../targets';
import { onKeyGate, pressKey } from '../keyGate';
import { playCoins } from '../coinSound';
import { useTourCss } from './tourCss';

/**
 * The guided first visit's pieces, as the owner's prototype draws them (prototypes/guided-journey: world/Panels.jsx
 * NextStepCard, world/QuikbaseTour.jsx Spotlight + MoveKeys + LookHint, world/MicFlow.jsx, world/BadgeMoment.jsx, the
 * LeaveSheet). Web only; the native build gets nothing (TourPieces.js).
 */

// lucide icons the shared set does not have (same paths as lucide-react-native)
const svg = (children, size = 24) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const Briefcase = ({ size }) => svg(<><path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><rect width="20" height="14" x="2" y="6" rx="2" /></>, size);
const MessageCircle = ({ size }) => svg(<path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />, size);
const MicOff = ({ size }) => svg(<><path d="M12 19v3" /><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" /><path d="M16.95 16.95A7 7 0 0 1 5 12v-2" /><path d="M18.89 13.23A7 7 0 0 0 19 12v-2" /><path d="m2 2 20 20" /><path d="M9 9v3a3 3 0 0 0 5.12 2.12" /></>, size);
const RotateCcw = ({ size }) => svg(<><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></>, size);
const Sliders = ({ size }) => svg(<><path d="M10 5H3" /><path d="M12 19H3" /><path d="M14 3v4" /><path d="M16 17v4" /><path d="M21 12h-9" /><path d="M21 19h-5" /><path d="M21 5h-7" /><path d="M8 10v4" /><path d="M8 12H3" /></>, size);
const Lock = ({ size }) => svg(<><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>, size);
const Keyboard = ({ size }) => svg(<><path d="M10 8h.01" /><path d="M12 12h.01" /><path d="M14 8h.01" /><path d="M16 12h.01" /><path d="M18 8h.01" /><path d="M6 8h.01" /><path d="M7 16h10" /><path d="M8 12h.01" /><rect width="20" height="16" x="2" y="4" rx="2" /></>, size);
const House = ({ size }) => svg(<><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>, size);
const Globe = ({ size }) => svg(<><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></>, size);

const ICONS = {
  menu: p => <BookOpen size={p.size} color="currentColor" />,
  quiz: p => <ClipboardCheck size={p.size} color="currentColor" />,
  store: Briefcase,
  tutor: MessageCircle,
  upgrade: p => <Sparkles size={p.size} color="currentColor" />,
  leave: p => <LogOut size={p.size} color="currentColor" />,
  explore: Globe,
};

/** "What's next": one card at the bottom naming the one control that pulses (Sol journey-v3 Q2; owner 9 Oct). */
export function NextStepCard({ icon = 'menu', title, children }) {
  useTourCss();
  const Icon = ICONS[icon] || ICONS.menu;
  return (
    <div className="qlt-next" data-testid="first-visit-next">
      <div className="qlt-next-eyebrow"><span className="qlt-next-dot" /> What&apos;s next</div>
      <div className="qlt-next-row">
        <div className="qlt-next-icon"><Icon size={30} /></div>
        <div style={{ flex: 1, minWidth: 0 }}><div className="qlt-next-title">{title}</div></div>
      </div>
      {children}
    </div>
  );
}

function useRect(targetId) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!targetId) return undefined;
    let frame = 0;
    const tick = () => {
      const el = findTarget(targetId);
      const b = el ? el.getBoundingClientRect() : null;
      setRect(p => (!b || !b.width ? null : p && Math.abs(p.x - b.x) < 0.5 && Math.abs(p.y - b.y) < 0.5 && Math.abs(p.w - b.width) < 0.5 && Math.abs(p.h - b.height) < 0.5 ? p : { x: b.x, y: b.y, w: b.width, h: b.height }));
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [targetId]);
  return rect;
}

/**
 * Quikbase: the screen dimmed around one control (a lit hole) and a coach card beside it - "Step x of 7", a title, a
 * line. outline=false: the card only (the walk-in step names the portal, it is not a control); handRoom: the hand stands
 * beside the target, so the card keeps clear of it. No target: the card at a fixed top-centre spot (Sol round13 R2 A).
 */
export function Spotlight({ targetId, side = 'top', step, total, title, body, pad = 12, dim = true, outline = true, handRoom = false, children }) {
  useTourCss();
  const rect = useRect(targetId);
  const W = 360;
  if (targetId && !rect) return null;
  const vw = window.innerWidth;
  let card = { left: vw / 2 - W / 2, top: 84 };
  let hole = null;
  if (rect) {
    hole = { left: rect.x - pad, top: rect.y - pad, width: rect.w + 2 * pad, height: rect.h + 2 * pad };
    const room = handRoom ? 64 : 0;
    const clampX = x => Math.min(vw - W - 16, Math.max(16, x));
    card = side === 'left'
      ? { left: Math.max(16, hole.left - W - 24 - room), top: Math.max(16, hole.top + hole.height / 2 - 90) }
      : side === 'right'
        ? { left: clampX(hole.left + hole.width + 24), top: Math.max(16, hole.top + hole.height / 2 - 90) }
        : side === 'below'
          ? { left: clampX(hole.left + hole.width / 2 - W / 2), top: hole.top + hole.height + 20 }
          : { left: clampX(hole.left + hole.width / 2 - W / 2), bottom: window.innerHeight - hole.top + 20 };
  }
  return (
    <>
      {hole && outline ? <div className={`qlt-hole ${dim ? '' : 'no-dim'}`} style={hole} /> : null}
      <div className="qlt-coach" style={card} data-testid="first-visit-coach">
        {step != null ? <div className="qlt-coach-step">Step {step} of {total}</div> : null}
        <div className="qlt-coach-title">{title}</div>
        {body ? <div className="qlt-coach-body">{body}</div> : null}
        {children}
      </div>
    </>
  );
}

const KEY_ROWS = [['W'], ['A', 'S', 'D'], ['Space']];
/**
 * The on-screen W A S D + Space (owner rounds 10-11, 14.5, 14.6): the key to press shows itself being pressed; while the
 * tour guides, only that key works - a click on it (or the real key) is one fixed step; any other key greys out with a
 * "no" sign. Shown only while the Quikbase tour runs (owner 15.4).
 */
export function MoveKeysPad({ hint = null, allow = null }) {
  useTourCss();
  const [lit, setLit] = useState({});
  const [denied, setDenied] = useState({});
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const flash = (setter, name, ms) => {
    setter(s => ({ ...s, [name]: (s[name] || 0) + 1 }));
    timers.current.push(setTimeout(() => setter(s => { const n = { ...s }; delete n[name]; return n; }), ms));
  };
  useEffect(() => onKeyGate(event => {
    if (event.type === 'denied') flash(setDenied, event.name, 900);
    else flash(setLit, event.name, 260);
  }), []);
  const press = name => {
    if (allow === null || allow === name) pressKey(name);
    else flash(setDenied, name, 900);
  };
  return (
    <div className="qlt-keys" data-testid="first-visit-keys">
      {KEY_ROWS.map(row => (
        <div key={row.join('')} className="qlt-keys-row">
          {row.map(name => {
            const cls = ['qlt-key', name === 'Space' ? 'space' : '', lit[name] ? 'on' : '', hint === name && !lit[name] ? 'hint' : '', denied[name] ? 'denied' : '', allow !== null && allow !== name ? 'locked' : '']
              .filter(Boolean).join(' ');
            return (
              <button key={name} type="button" className={cls} aria-label={name === 'Space' ? 'Jump (Space)' : `Walk (${name})`}
                onPointerDown={event => { event.preventDefault(); press(name); }}>
                {name}
                {denied[name] ? <span key={denied[name]} className="qlt-key-no" aria-hidden="true" /> : null}
              </button>
            );
          })}
        </div>
      ))}
      <div className="qlt-keys-note">or the arrow keys · Space jumps</div>
    </div>
  );
}

/** Look around: a mouse OR a trackpad (owner round 13.10). */
export function LookHint() {
  useTourCss();
  return (
    <div className="qlt-look" id="first-visit-look" data-testid="first-visit-look">
      <div className="qlt-look-ways">
        <div className="qlt-look-way">
          <svg width="44" height="62" viewBox="0 0 64 88" fill="none" aria-hidden="true">
            <rect x="8" y="4" width="48" height="80" rx="24" stroke="#c5e836" strokeWidth="3" />
            <path d="M32 4 V36 M8 36 H56" stroke="#c5e836" strokeWidth="3" />
            <path d="M10 34 V28 A22 22 0 0 1 32 6 V34 Z" fill="rgba(197,232,54,.55)" />
          </svg>
          <span>Mouse</span>
        </div>
        <div className="qlt-look-or">OR</div>
        <div className="qlt-look-way">
          <svg width="78" height="62" viewBox="0 0 110 88" fill="none" aria-hidden="true">
            <rect x="4" y="8" width="102" height="72" rx="12" stroke="#c5e836" strokeWidth="3" />
            <circle className="qlt-look-dot" cx="36" cy="44" r="9" fill="rgba(197,232,54,.75)" />
          </svg>
          <span>Trackpad</span>
        </div>
      </div>
      <div className="qlt-look-arrows">⟵ drag ⟶</div>
    </div>
  );
}

/**
 * The microphone (owner 11 Oct): "a simple text message should be enough ... first show the message and then trigger
 * permission so that they are aware they need to allow it". The message, then - a moment later, with no click - the
 * browser's own box. The microphone is released at once; the tutor session opens its own.
 */
export const MIC_MESSAGE = 'To talk to your AI tutor, allow microphone access so you can speak to your tutor.';
const ASK_AFTER_MS = 1500;
export async function requestMic() {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach(track => track.stop());
}
export function MicMessage({ onAllowed, onBlocked }) {
  useTourCss();
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      requestMic()
        .then(() => live && onAllowed())
        .catch(error => live && onBlocked(error && error.name));
    }, ASK_AFTER_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="qlt-mic-bar" data-testid="first-visit-mic-message">
      <span className="qlt-mic-ico"><Mic size={20} color="currentColor" /></span>
      <div>
        <div className="qlt-mic-text">{MIC_MESSAGE}</div>
        {/* owner 11 Oct: "Click Allow while visiting the site when your browser asks" */}
        <div className="qlt-mic-hint">Click <b>Allow</b> while visiting the site when your browser asks.</div>
      </div>
    </div>
  );
}

/** Blocked or dismissed: how to allow it, Try again; after the second failed try, typing too (Sol voice-first V5 A). */
export function MicHelp({ tries, noDevice = false, onRetry, onType }) {
  useTourCss();
  const [checking, setChecking] = useState(false);
  const retry = () => {
    if (checking) return;
    setChecking(true);
    requestMic()
      .then(() => onRetry(null))
      .catch(error => {
        setChecking(false);
        onRetry((error && error.name) || 'NotAllowedError');
      });
  };
  return (
    <div className="qlt-scrim" data-testid="first-visit-mic-help">
      <div className="qlt-card">
        <span className="qlt-card-ico"><MicOff size={26} /></span>
        <div className="qlt-card-title">{noDevice ? 'No microphone found' : 'Your microphone is blocked'}</div>
        <div className="qlt-card-body">
          {noDevice ? 'Plug in a microphone or headset, then click Try again.' : 'To talk to your AI tutor, turn the microphone on in your browser, then click Try again.'}
        </div>
        {!noDevice ? (
          <div className="qlt-steps">
            <div className="qlt-step"><span className="qlt-n">1</span><span className="qlt-pill"><Sliders size={13} /> <Lock size={11} /> app.quiklearn.ai</span><span>Click the icon left of the web address</span></div>
            <div className="qlt-step"><span className="qlt-n">2</span><span className="qlt-pill"><Mic size={13} color="currentColor" /> Microphone <i className="qlt-switch" /></span><span>Turn <b>Microphone</b> on (Allow)</span></div>
            <div className="qlt-step"><span className="qlt-n">3</span><span className="qlt-pill"><RotateCcw size={13} /> Try again</span><span>Come back here and click <b>Try again</b></span></div>
          </div>
        ) : null}
        <button type="button" className="qlt-cta" data-testid="first-visit-mic-retry" onClick={retry} disabled={checking}>
          <RotateCcw size={18} /> {checking ? 'Checking…' : 'Try again'}
        </button>
        {!checking ? <FocusRing targetId="first-visit-mic-retry" /> : null}
        {tries >= 2 ? (
          <button type="button" className="qlt-alt" data-testid="first-visit-mic-type" onClick={onType}><Keyboard size={15} /> Type my question instead</button>
        ) : null}
      </div>
    </div>
  );
}

/** "Where to next?" - leaving a unit world (Sol dev-port D4 A). focusCourses: the tour's last step points at Home. */
export function LeaveSheet({ focusCourses = false, onCourses, onQuikbase, onClose }) {
  useTourCss();
  return (
    <div className="qlt-scrim" data-testid="leave-sheet" onClick={event => event.target === event.currentTarget && onClose()}>
      <div className="qlt-sheet">
        <button type="button" className="qlt-sheet-close" aria-label="Close" onClick={onClose}><X size={18} color="#fff" /></button>
        <div className="qlt-sheet-title">Where to next?</div>
        <div className="qlt-sheet-sub">Your lesson is saved. You can come back any time.</div>
        <button type="button" className="qlt-option" data-testid="leave-sheet-courses" onClick={onCourses}>
          <span className="qlt-option-ico"><House size={24} /></span>
          <span><div className="qlt-option-title">Back to my courses</div><div className="qlt-option-sub">Your dashboard</div></span>
        </button>
        <button type="button" className="qlt-option" data-testid="leave-sheet-quikbase" onClick={onQuikbase}>
          <span className="qlt-option-ico"><Globe size={24} /></span>
          <span><div className="qlt-option-title">Go to Quikbase</div><div className="qlt-option-sub">The hub with every world</div></span>
        </button>
        <button type="button" className="qlt-stay" onClick={onClose}>Stay in the lesson</button>
        {focusCourses ? <FocusRing targetId="leave-sheet-courses" /> : null}
      </div>
    </div>
  );
}

/**
 * A badge earned (the prototype's moment, owner rounds 6-13): rays and sparks, the badge, its name and line, its
 * QuikCoins, then Collect - outlined with the hand (round 13.3) - and it flies into the Collection Store.
 */
// the badge leaves for the Collection Store: it starts where it was and moves there on the next frame
function FlyingBadge({ src, to }) {
  const [gone, setGone] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setGone(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <img className="qlt-bm-fly" src={src} alt=""
      style={{ width: 260, height: 260, left: window.innerWidth / 2 - 130, top: window.innerHeight / 2 - 170,
        transform: gone ? `translate(${to.x}px, ${to.y + 40}px) scale(0.12)` : 'none', opacity: gone ? 0 : 1 }} />
  );
}

export function TourBadgeMoment({ badge, onDone }) {
  useTourCss();
  const [phase, setPhase] = useState('intro'); // intro | badge | coins | ready | flying
  const [flight, setFlight] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase('badge'), 400),
      setTimeout(() => {
        if (badge.coins > 0) {
          setPhase('coins');
          playCoins(4);
        }
      }, 1900),
      setTimeout(() => setPhase('ready'), badge.coins > 0 ? 3000 : 1900),
    ];
    return () => timers.forEach(clearTimeout);
  }, [badge]);
  const sparks = useMemo(() => Array.from({ length: 26 }, (_, i) => {
    const a = (i / 26) * Math.PI * 2 + Math.random() * 0.3;
    const d = 170 + Math.random() * 140;
    return { id: i, x: Math.cos(a) * d, y: Math.sin(a) * d, s: 6 + Math.random() * 8, c: ['#fcd34d', '#c5e836', '#fff', '#a78bfa'][i % 4] };
  }), []);
  const collect = () => {
    setFlight(flightToStore());
    setPhase('flying');
    if (badge.coins > 0) setTimeout(() => playCoins(3), 1000);
    setTimeout(() => onDone && onDone(), 1300);
  };
  const flying = phase === 'flying';
  return (
    <div className="qlt-bm" data-testid="badge-moment">
      <div className="qlt-bm-scrim" style={{ opacity: flying ? 0 : 1 }} />
      {!flying ? (
        <>
          <div className="qlt-bm-eyebrow">Badge earned</div>
          <div className="qlt-bm-stage">
            {phase !== 'intro' ? <div className="qlt-bm-rays" /> : null}
            {phase !== 'intro'
              ? sparks.map(s => (
                <span key={s.id} className="qlt-bm-spark" style={{ width: s.s, height: s.s, background: s.c, '--x': `${s.x}px`, '--y': `${s.y}px` }} />
              ))
              : null}
            {phase !== 'intro' && badge.imageUrl ? <img className="qlt-bm-badge" src={badge.imageUrl} alt={badge.name} /> : null}
          </div>
          {phase !== 'intro' ? (
            <>
              <div className="qlt-bm-title">{badge.name}</div>
              {badge.description ? <div className="qlt-bm-sub">{badge.description}</div> : null}
            </>
          ) : null}
          <div style={{ height: 78 }}>
            {badge.coins > 0 && (phase === 'coins' || phase === 'ready') ? (
              <div className="qlt-bm-reward">
                <span className="qlt-bm-coins">
                  {[0, 1, 2].map(i => <span key={i} className="qlt-coin" style={{ animationDelay: `${i * 0.12}s` }}>Q</span>)}
                </span>
                {`+${badge.coins} QuikCoins`}
              </div>
            ) : null}
          </div>
          {phase === 'ready' ? (
            <>
              <button type="button" className="qlt-cta" data-testid="badge-collect" onClick={collect}>Collect</button>
              <FocusRing targetId="badge-collect" />
            </>
          ) : null}
        </>
      ) : null}
      {flying && badge.imageUrl ? <FlyingBadge src={badge.imageUrl} to={flight} /> : null}
    </div>
  );
}

/**
 * The tour quiz on today's quiz board, titled "Quick check" (Sol proto-port P5 A; the owner's prototype round 8): the
 * hand points at the right answer, then Submit Answer, then Next / Finish; only the right answer can be picked. These
 * answers never count toward mastery, so the header shows a score instead of "Mastery Progress".
 */
export function TourQuizBoard({ entry, onDone }) {
  useTourCss();
  const questions = (entry && entry.quiz) || [];
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState({});
  const [submitted, setSubmitted] = useState({});
  const q = questions[i];
  if (!q) return null;
  const sel = picked[i];
  const isSub = submitted[i] != null;
  const done = Object.keys(submitted).length;
  const score = Object.values(submitted).filter(Boolean).length;
  const last = i === questions.length - 1;
  const submit = () => {
    if (sel == null || isSub) return;
    setSubmitted(s => ({ ...s, [i]: sel === q.answer }));
  };
  const next = () => {
    if (!last) setI(i + 1);
    else if (done === questions.length && onDone) onDone();
  };
  const ring = !isSub ? (sel == null ? `first-visit-quiz-option-${q.options.indexOf(q.answer)}` : 'first-visit-quiz-submit') : 'first-visit-quiz-next';
  return (
    <div className="qlt-qz" data-testid="first-visit-quiz" style={q.image ? { backgroundImage: `url(${q.image})` } : { background: '#0b0d12' }}>
      <div className="qlt-qz-col">
        <div className="qlt-qz-head">
          <div className="qlt-qz-h1">Quick check</div>
          <div className="qlt-qz-points"><b>{`Score: ${score}/${questions.length}`}</b>
            <div className="qlt-qz-bar"><i style={{ width: `${(score / questions.length) * 100}%` }} /><span>{`${Math.round((score / questions.length) * 100)} %`}</span></div>
          </div>
        </div>
        <div className="qlt-qz-qrow">
          <span>{`Question ${i + 1}/${questions.length}`}</span>
          <span className="qlt-qz-dots">{questions.map((_, k) => <i key={k} className={k === i ? 'on' : ''} />)}</span>
        </div>
        <div className="qlt-qz-card" key={i}>
          <div className="qlt-qz-chips"><span>MCQ</span><span>1 Point</span><span className="report">Report</span></div>
          <div className="qlt-qz-q">{q.question}</div>
          {q.options.map((o, k) => {
            const state = !isSub ? (sel === o ? 'picked' : '') : o === q.answer ? 'right' : '';
            return (
              <button key={o} type="button" className={`qlt-qz-opt ${state}`} disabled={isSub} data-testid={`first-visit-quiz-option-${k}`}
                onClick={() => { if (o === q.answer) setPicked(p => ({ ...p, [i]: o })); }}>
                <span className="qlt-qz-radio">{sel === o || state === 'right' ? <i /> : null}</span>{o}
              </button>
            );
          })}
          {!isSub ? (
            <button type="button" className={`qlt-qz-submit ${sel == null ? 'off' : ''}`} data-testid="first-visit-quiz-submit" onClick={submit} disabled={sel == null}>
              Submit Answer
            </button>
          ) : (
            <div className="qlt-qz-explain"><b>Explanation:</b><div>{q.explanation}</div></div>
          )}
        </div>
        <div className="qlt-qz-nav">
          <button type="button" className="qlt-qz-prev" onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0}>‹ Previous</button>
          <button type="button" className={`qlt-qz-next ${isSub ? 'ready' : ''}`} data-testid="first-visit-quiz-next" onClick={next} disabled={!isSub || (last && done !== questions.length)}>
            {last ? 'Finish' : 'Next'} ›
          </button>
        </div>
      </div>
      <FocusRing key={ring} targetId={ring} />
    </div>
  );
}
